import React, { useState, useCallback } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import axios from 'axios'
import Swal from 'sweetalert2'
import { withApiBaseUrl } from '../lib/apiBaseUrl'

interface TestResult {
  success: boolean
  data?: any
  error?: string
  timestamp: string
}

const TestFirebaseSMSPage: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('+66616254799')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const [testResults, setTestResults] = useState<TestResult[]>([])

  // Firebase reCAPTCHA Site Key (ใส่ site key จริง)
  const RECAPTCHA_SITE_KEY = "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI" // Test key - แทนที่ด้วย site key จริง

  // Handle reCAPTCHA verification
  const handleRecaptchaChange = useCallback((token: string | null) => {
    setRecaptchaToken(token)
    console.log('📝 reCAPTCHA token received:', token ? `${token.substring(0, 20)}...` : 'null')
  }, [])

  // Add test result to history
  const addTestResult = (result: Omit<TestResult, 'timestamp'>) => {
    const newResult: TestResult = {
      ...result,
      timestamp: new Date().toLocaleString()
    }
    setTestResults(prev => [newResult, ...prev].slice(0, 10)) // Keep last 10 results
  }

  // Test Firebase SMS
  const handleTestFirebaseSMS = async () => {
    if (!recaptchaToken) {
      await Swal.fire({
        icon: 'warning',
        title: 'reCAPTCHA Required',
        text: 'กรุณายืนยัน reCAPTCHA ก่อนส่ง SMS',
        confirmButtonText: 'ตกลง'
      })
      return
    }

    setIsSubmitting(true)

    try {
      console.log('🔥 Testing Firebase SMS with reCAPTCHA token:', {
        phoneNumber,
        hasRecaptchaToken: !!recaptchaToken,
        tokenPreview: recaptchaToken ? `${recaptchaToken.substring(0, 20)}...` : 'none'
      })

      // Call SMS Service API via Load Balancer
      const response = await axios.post(withApiBaseUrl('/api/v1/sms/otp/send'), {
        tenantId: 'test-tenant',
        phoneNumber: phoneNumber,
        recaptchaToken: recaptchaToken,
        messageType: 'otp'
      }, {
        headers: {
          'Content-Type': 'application/json',
          'X-Service-Token': 'HuayServiceToken_V4_9kX3mN7wQ8zR5pY2vB6nC1sF4jH8gL0dA9',
          'X-Service-Name': 'user-auth'
        }
      })

      console.log('✅ Firebase SMS Test Success:', response.data)

      addTestResult({
        success: true,
        data: response.data
      })

      await Swal.fire({
        icon: 'success',
        title: 'Firebase SMS ส่งสำเร็จ! 🎉',
        html: `
          <div style="text-align: left; font-family: monospace; background: #f5f5f5; padding: 10px; border-radius: 5px;">
            <strong>Provider:</strong> ${response.data.provider || 'firebase'}<br/>
            <strong>Message ID:</strong> ${response.data.messageId || 'N/A'}<br/>
            <strong>Phone:</strong> ${response.data.phoneNumber || phoneNumber}<br/>
            <strong>OTP ID:</strong> ${response.data.otpId || 'N/A'}
          </div>
        `,
        confirmButtonText: 'เยี่ยม!',
        confirmButtonColor: '#28a745'
      })

    } catch (error: any) {
      console.error('❌ Firebase SMS Test Failed:', error)

      const errorMessage = error.response?.data?.message || error.message || 'Unknown error'
      const errorCode = error.response?.data?.error || 'UNKNOWN_ERROR'

      addTestResult({
        success: false,
        error: `${errorCode}: ${errorMessage}`
      })

      await Swal.fire({
        icon: 'error',
        title: 'Firebase SMS ล้มเหลว 😞',
        html: `
          <div style="text-align: left; font-family: monospace; background: #fff3cd; padding: 10px; border-radius: 5px;">
            <strong>Error Code:</strong> ${errorCode}<br/>
            <strong>Message:</strong> ${errorMessage}<br/>
            <strong>Status:</strong> ${error.response?.status || 'Network Error'}
          </div>
        `,
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#dc3545'
      })
    } finally {
      setIsSubmitting(false)
      // Reset reCAPTCHA for next test
      setRecaptchaToken(null)
    }
  }

  // Format phone number input
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value
    // Allow + and numbers only
    value = value.replace(/[^\d+]/g, '')
    setPhoneNumber(value)
  }

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        color: 'white', 
        padding: '20px', 
        borderRadius: '10px',
        marginBottom: '20px'
      }}>
        <h1 style={{ margin: 0, fontSize: '24px' }}>🔥 Firebase SMS + reCAPTCHA Test</h1>
        <p style={{ margin: '10px 0 0 0', opacity: 0.9 }}>
          ทดสอบการส่ง SMS ผ่าน Firebase ด้วย reCAPTCHA token
        </p>
      </div>

      {/* Test Form */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '10px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <h3 style={{ marginTop: 0, color: '#333' }}>📱 ข้อมูลการทดสอบ</h3>
        
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            หมายเลขโทรศัพท์:
          </label>
          <input
            type="text"
            value={phoneNumber}
            onChange={handlePhoneChange}
            placeholder="+66812345678"
            style={{
              width: '100%',
              padding: '10px',
              border: '2px solid #ddd',
              borderRadius: '5px',
              fontSize: '16px'
            }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
            reCAPTCHA Verification:
          </label>
          <ReCAPTCHA
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={handleRecaptchaChange}
            onExpired={() => setRecaptchaToken(null)}
            onErrored={() => setRecaptchaToken(null)}
          />
          {recaptchaToken && (
            <div style={{ 
              marginTop: '10px', 
              padding: '10px', 
              background: '#d4edda', 
              borderRadius: '5px',
              fontSize: '14px'
            }}>
              ✅ reCAPTCHA verified: {recaptchaToken.substring(0, 30)}...
            </div>
          )}
        </div>

        <button
          onClick={handleTestFirebaseSMS}
          disabled={isSubmitting || !recaptchaToken}
          style={{
            backgroundColor: !recaptchaToken ? '#6c757d' : (isSubmitting ? '#ffc107' : '#28a745'),
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '5px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: !recaptchaToken ? 'not-allowed' : (isSubmitting ? 'wait' : 'pointer'),
            width: '100%'
          }}
        >
          {isSubmitting ? '🔄 กำลังส่ง SMS...' : (
            !recaptchaToken ? '🔒 กรุณายืนยัน reCAPTCHA ก่อน' : '🚀 ทดสอบ Firebase SMS'
          )}
        </button>
      </div>

      {/* Test Results */}
      {testResults.length > 0 && (
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '10px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginTop: 0, color: '#333' }}>📊 ผลการทดสอบ</h3>
          {testResults.map((result, index) => (
            <div
              key={index}
              style={{
                background: result.success ? '#d4edda' : '#f8d7da',
                border: `1px solid ${result.success ? '#c3e6cb' : '#f5c6cb'}`,
                borderRadius: '5px',
                padding: '10px',
                marginBottom: '10px',
                fontSize: '14px'
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                {result.success ? '✅ สำเร็จ' : '❌ ล้มเหลว'} - {result.timestamp}
              </div>
              {result.success && result.data && (
                <pre style={{ margin: 0, fontSize: '12px', background: 'rgba(0,0,0,0.05)', padding: '5px', borderRadius: '3px' }}>
                  {JSON.stringify(result.data, null, 2)}
                </pre>
              )}
              {!result.success && result.error && (
                <div style={{ color: '#721c24', fontFamily: 'monospace' }}>
                  {result.error}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Instructions */}
      <div style={{
        background: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '10px',
        padding: '20px',
        marginTop: '20px'
      }}>
        <h4 style={{ marginTop: 0, color: '#856404' }}>📋 วิธีการใช้งาน:</h4>
        <ol style={{ color: '#856404' }}>
          <li>กรอกหมายเลขโทรศัพท์ที่ต้องการทดสอบ</li>
          <li>กดปุ่ม reCAPTCHA เพื่อยืนยันว่าคุณไม่ใช่บอท</li>
          <li>กดปุ่ม "ทดสอบ Firebase SMS" เพื่อส่ง SMS</li>
          <li>ตรวจสอบผลลัพธ์ในส่วน "ผลการทดสอบ"</li>
          <li>หาก Firebase ทำงานได้ - จะได้รับ SMS จริงๆ! 🎉</li>
        </ol>
      </div>
    </div>
  )
}

export default TestFirebaseSMSPage
